import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    TextField
} from "@material-ui/core";
import {
    AttachFile,
    Close,
    CloudUpload,
    ExpandMore,
    Reply,
    Send
} from "@material-ui/icons";
import { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import {
    FaCalendarAlt,
    FaDownload,
    FaEye,
    FaFileAlt,
    FaRegPaperPlane,
    FaReply,
    FaUser,
} from "react-icons/fa";
import { IoRepeat } from "react-icons/io5";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { toast } from "react-toastify";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import {
    getCurrentUserEmail,
    getCurrentUserId,
    getOrganizationId,
} from "../../../utils";
import { getAllUsersAPI } from "../Dashboard/api";
import { getMessage, replyMessage, sendMessage, updateMessage, updateMessageReplyRead } from "./action";

const allowedExtensions = ["pdf", "docx", "csv", "xls", "xlsx"];

// Custom Tabs Component with Unread Badges
const CustomTabs = ({ tabs, active, setActive, unreadCounts = {} }) => {
    return (
        <div className="flex gap-2">
            {tabs.map((tab) => {
                const isActive = active === tab.key;
                const unreadCount = unreadCounts[tab.key] || 0;

                return (
                    <button
                        key={tab.key}
                        onClick={() => setActive(tab.key)}
                        className={`
                            relative h-8 px-4 font-medium text-[11px] uppercase tracking-wider rounded-none transition-all duration-150 flex items-center gap-2
                            ${isActive
                                ? 'bg-[#313C4C] text-white shadow-xs z-5 border border-[#313C4C]'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200/80 border border-slate-200'
                            }
                        `}
                    >
                        <span>{tab.label}</span>

                        {/* Unread Count Badge */}
                        {unreadCount > 0 && (
                            <span className={`
                                min-w-[18px] h-4 px-1.5 flex items-center justify-center
                                bg-rose-600 text-white text-[11px] font-medium rounded-none
                                ${isActive ? 'bg-rose-500' : ''}
                            `}>
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

const Messagebox = () => {
    const [activeTab, setActiveTab] = useState(1);
    const [expandedReplyId, setExpandedReplyId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const history = useHistory();
    const [mode, setMode] = useState("send");

    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get("id");

    const tabs = [
        { key: 1, label: "My Inbox" },
        { key: 2, label: "Sent Messages" },
    ];

    const [docType, setDocType] = useState("");
    const [messageTitle, setMessageTitle] = useState("");
    const [messageBody, setMessageBody] = useState("");
    const [sendTo, setSendTo] = useState("anyone");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedUser, setSelectedUser] = useState({
        email: "",
        _id: "",
    });
    const [file, setFile] = useState(null);

    const [userList, setUserList] = useState({
        everyone: [],
        org: [],
    });

    const [messages, setMessages] = useState({
        sendedMsg: [],
        myInbox: [],
    });
    const [hasFetched, setHasFetched] = useState(false);

    const currentUserId = getCurrentUserId();

    const unreadCounts = {
        1: messages.myInbox.reduce((count, msg) => {
            const unreadMain = (!msg.isRead && msg.sendedBy?._id !== currentUserId) ? 1 : 0;
            const unreadReplies = msg.replies ? msg.replies.filter(
                reply => !reply.isRead && reply.sendedBy?._id !== currentUserId
            ).length : 0;
            return count + unreadMain + unreadReplies;
        }, 0),

        2: messages.sendedMsg.reduce((count, msg) => {
            const unreadMain = (!msg.isRead && msg.sendedBy?._id !== currentUserId) ? 1 : 0;
            const unreadReplies = msg.replies ? msg.replies.filter(
                reply => !reply.isRead && reply.sendedBy?._id !== currentUserId
            ).length : 0;
            return count + unreadMain + unreadReplies;
        }, 0),
    };

    const fetchMessages = async () => {
        const response = await getMessage({ id: getCurrentUserId() });
        if (response?.data) {
            setMessages({
                sendedMsg: response.data.sendedMsg || [],
                myInbox: response.data.myInbox || [],
            });
        }
        return response.data.myInbox;
    };

    const fetchUsersDetails = async () => {
        const user = await getAllUsersAPI();
        const currentEmail = getCurrentUserEmail();
        const orgUsers = user?.data?.list?.filter(
            (item) =>
                item?.organizationId === getOrganizationId() &&
                item?.email !== currentEmail
        );

        setUserList({
            everyone:
                user?.data?.list
                    ?.filter((item) => item.email !== currentEmail && item.isEmailPrivate !== true)
                    .map((item) => ({
                        email: item?.email,
                        _id: item?._id,
                    })) || [],
            org:
                orgUsers
                    ?.filter((item) => item.email !== currentEmail && item.isEmailPrivate !== true)
                    .map((item) => ({
                        email: item?.email,
                        _id: item?._id,
                    })) || [],
        });
    };

    const handleInitialFetch = async () => {
        await fetchUsersDetails();
        const inbox = await fetchMessages();
        if (id) {
            const message = inbox.find((item) => item._id === id);
            if (message) {
                setSelectedMessage(message);
                if (message.isRead === false) {
                    setMessages((prev) => ({
                        ...prev,
                        myInbox: prev.myInbox.map((item) =>
                            item._id === message._id ? { ...item, isRead: true } : item
                        ),
                    }));
                    updateMessage({ id: message._id }, { isRead: true });
                }
            }
        }
        setHasFetched(true);
    };

    useEffect(() => {
        const interval = setInterval(handleInitialFetch, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        handleInitialFetch();
    }, []);

    const filteredUsers =
        sendTo === "anyone"
            ? userList.everyone.filter((u) =>
                u.email.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : userList.org.filter((u) =>
                u.email.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const handleSend = async () => {
        if (!messageTitle || !selectedUser._id || !file) {
            toast.error("Please fill all the fields");
            return;
        }
        const formData = new FormData();
        formData.append("docType", docType);
        formData.append("sendedBy", getCurrentUserId());
        formData.append("messageTitle", messageTitle);
        formData.append("messageBody", messageBody);
        formData.append("sendTo", selectedUser._id);
        formData.append("files", file);
        formData.append(
            "fileType",
            allowedExtensions.includes(file?.type?.split("/").pop())
                ? file?.type?.split("/").pop()
                : allowedExtensions.includes(file.name.split(".").pop())
                    ? file.name.split(".").pop()
                    : "pdf"
        );
        if (!formData.get("fileType")) {
            toast.error("Please select a valid file type");
            return;
        }
        await sendMessage(formData);
        setIsModalOpen(false);
        resetForm();
        fetchMessages();
        toast.success("Message sent successfully");
    };

    const handleOpenReply = (message) => {
        setMode("reply");
        setSelectedMessage(message);
        setMessageTitle(message.messageTitle || "");
        setMessageBody("");
        setDocType(message.docType || "");
        setSendTo("anyone");
        setSelectedUser({
            email: message.sendedBy?.email,
            _id: message.sendedBy?._id,
        });
        setIsModalOpen(true);
    };

    const handleSendReply = async (file = null) => {
        if (!messageTitle || !selectedUser._id) {
            toast.error("Please fill all the fields");
            return;
        }
        const formData = new FormData();
        formData.append("docType", docType);
        formData.append("sendedBy", getCurrentUserId());
        formData.append("messageTitle", messageTitle);
        formData.append("messageBody", messageBody);
        formData.append("sendTo", selectedUser._id);
        if (file) formData.append("files", file);
        formData.append("replyMessageId", selectedMessage?.replyMessageId);
        if (file) {
            formData.append(
                "fileType",
                allowedExtensions.includes(file?.type?.split("/").pop())
                    ? file?.type?.split("/").pop()
                    : allowedExtensions.includes(file.name.split(".").pop())
                        ? file.name.split(".").pop()
                        : "pdf"
            );
        }
        if (!formData.get("fileType") && file) {
            toast.error("Please select a valid file type");
            return;
        }
        await replyMessage({ id: selectedMessage?._id }, formData);
        setIsModalOpen(false);
        resetForm();
        fetchMessages();
        toast.success("Reply sent successfully");
    };

    const resetForm = () => {
        setMessageTitle("");
        setDocType("");
        setSendTo("anyone");
        setSearchTerm("");
        setSelectedUser({
            email: "",
            _id: "",
        });
        setSelectedMessage(null);
        setFile(null);
    };

    useEffect(() => {
        setSearchTerm("");
        setSelectedUser({
            email: "",
            _id: "",
        });
    }, [sendTo]);

    const handleChange = (file) => {
        setFile(file);
    };

    const handleDownload = async (fileUrl, fileName) => {
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            let extension = fileUrl.split(".").pop().split("?")[0].toLowerCase();
            if (!allowedExtensions.includes(extension)) {
                extension = "pdf";
            }

            let finalName;
            if (fileName) {
                const nameWithoutExt = fileName.includes(".")
                    ? fileName.substring(0, fileName.lastIndexOf("."))
                    : fileName;
                finalName = `${nameWithoutExt}.${extension}`;
            } else {
                finalName = `document.${extension}`;
            }

            const link = document.createElement("a");
            link.href = url;
            link.download = finalName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
            toast.error("Failed to download file");
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getDocTypeLabel = (docType) => {
        const docTypeMap = {
            sof: "SOF / Laytime",
            cargo: "Cargo",
            vessel: "Vessel",
            invoice: "Invoice",
            contracts: "Contracts",
            address: "Address Book",
            matrix: "Matrix",
        };
        return docTypeMap[docType] || docType;
    };

    const handleViewDetails = (message, isInbox) => {
        setSelectedMessage(message);
        if (isInbox && message.isRead === false) {
            setMessages((prev) => ({
                ...prev,
                myInbox: prev.myInbox.map((item) =>
                    item._id === message._id ? { ...item, isRead: true } : item
                ),
            }));
            updateMessage({ id: message._id }, { isRead: true });
        }
    };

    const handleCloseDetailModal = () => {
        setSelectedMessage(null);
        setExpandedReplyId(null);
        if (id) {
            history.push(`/messagebox`);
        }
    };

    const getInitials = (email) => {
        return email?.split("@")[0]?.charAt(0)?.toUpperCase();
    };

    const renderMessageList = (messageList, isInbox) => {
        const getLatestReply = (replies) => {
            if (!replies || replies.length === 0) return null;
            const sortedReplies = [...replies].sort((a, b) =>
                new Date(b.createdAt) - new Date(a.createdAt)
            );
            return sortedReplies[0];
        };

        if (messageList.length === 0) {
            return hasFetched ? (
                <div className="flex flex-col items-center justify-center p-8 bg-slate-50/50 rounded-none border border-dashed border-slate-300 m-3">
                    <div className="bg-white p-3.5 rounded-none border border-slate-200 mb-3 shadow-xs">
                        <svg
                            className="w-8 h-8 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d={
                                    isInbox
                                        ? "M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                        : "M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                                }
                            />
                        </svg>
                    </div>
                    <h3 className="text-[11px] font-medium text-slate-700 mb-1 text-center">
                        {isInbox ? "No received messages" : "No sent messages"}
                    </h3>
                    <p className="text-slate-500 text-[11px] text-center max-w-xs font-medium">
                        {isInbox
                            ? "When someone sends you a message, it will appear here."
                            : "Messages you send will appear here for easy reference."}
                    </p>
                </div>
            ) : null;
        }

        return (
            <div className="space-y-1.5 px-0.5 py-0.5">
                {messageList.map((message) => {
                    const latestReply = getLatestReply(message.replies);

                    return (
                        <div
                            key={message._id}
                            className={`bg-white rounded-none border transition-all duration-200 hover:shadow-md hover:border-slate-300 cursor-pointer flex flex-col sm:flex-row sm:items-center p-1.5 sm:p-2 gap-2 ${isInbox && (!message.isRead || message.unreadRepliesCount > 0)
                                ? "border-l-4 border-l-[#313C4C] border-slate-200 bg-slate-50/70 shadow-xs"
                                : "border-slate-200/90 shadow-2xs"
                                }`}
                            onClick={() => handleViewDetails(message, isInbox)}
                        >
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                                <Avatar className="!w-7 !h-7 !text-[11px] !bg-[#313C4C] !text-white !font-medium !rounded-none shadow-xs">
                                    {getInitials(
                                        isInbox ? message.sendedBy?.email : message.sendTo.email
                                    )}
                                </Avatar>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-0.5 gap-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <h3
                                            className={`text-[11px] font-medium truncate ${isInbox && (!message.isRead || message.unreadRepliesCount > 0)
                                                ? "text-slate-900"
                                                : "text-slate-800"
                                                }`}
                                        >
                                            {message.messageTitle}
                                        </h3>
                                        {isInbox && (!message.isRead) && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-none text-[10px] font-medium tracking-wider bg-[#313C4C] text-white">
                                                NEW
                                            </span>
                                        )}
                                        {message.unreadRepliesCount > 0 && (
                                            <span
                                                className="inline-flex items-center px-1.5 py-0.5 rounded-none text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300 animate-pulse"
                                                aria-label={`Unread Replies: ${message.unreadRepliesCount}`}
                                            >
                                                {`${message.unreadRepliesCount} New Reply`}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2.5 text-[11px] font-medium text-slate-600 mb-0.5">
                                    <div className="flex items-center gap-1">
                                        <FaUser className="w-3 h-3 text-slate-400" />
                                        <span className="text-[11px] font-medium text-slate-800">
                                            {isInbox ? message.sendedBy?.email : message.sendTo.email}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <FaCalendarAlt className="w-3 h-3 text-slate-400" />
                                        <span className="text-[11px] font-medium">{formatDate(message.createdAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <FaFileAlt className="w-3 h-3 text-slate-400" />
                                        <span className="text-[11px] font-medium text-slate-700">{getDocTypeLabel(message.docType)}</span>
                                    </div>
                                </div>

                                {message.file && !latestReply && (
                                    <div className="flex items-center gap-1 text-[11px] font-medium text-slate-500 mt-0.5">
                                        <FaFileAlt className="w-3 h-3 text-slate-400" />
                                        <span>Attachment included</span>
                                    </div>
                                )}
                                {/* Latest Reply Preview */}
                                {latestReply && (
                                    <div className="mt-1 p-1 bg-slate-100/80 rounded-none border-l-3 border-[#313C4C] border border-slate-200/60 shadow-2xs">
                                        <div className="flex items-center gap-1 text-[10px] font-medium text-slate-500 mb-0.5">
                                            <FaReply className="w-2.5 h-2.5 text-[#313C4C]" />
                                            <span className="font-medium text-[#313C4C]">Latest Reply:</span>
                                        </div>
                                        <p className="text-[11px] text-slate-800 truncate font-medium">
                                            {latestReply.messageTitle}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Action Button */}
                            <div className="flex-shrink-0 mt-1 sm:mt-0">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewDetails(message);
                                    }}
                                    className="h-6 px-2.5 text-[10px] font-medium text-[#313C4C] border border-[#313C4C] hover:bg-[#313C4C] hover:text-white bg-white rounded-none uppercase flex items-center gap-1 transition-all shadow-2xs"
                                >
                                    <FaEye className="w-2.5 h-2.5" />
                                    <span>VIEW</span>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const getFileTypeClass = (fileType) => {
        switch (fileType?.toLowerCase()) {
            case "pdf":
            case "doc":
            case "docx":
                return "bg-[#313C4C]";
            case "xls":
            case "xlsx":
            case "csv":
                return "bg-emerald-600";
            default:
                return "bg-slate-600";
        }
    };

    const getFileTypeBorder = (fileType) => {
        switch (fileType) {
            case "pdf":
            case "doc":
            case "docx":
                return "border-t-slate-700";
            case "xls":
            case "xlsx":
            case "csv":
                return "border-t-emerald-600";
            default:
                return "border-t-slate-600";
        }
    };

    const uniqueReplies = selectedMessage && selectedMessage?.replies?.length
        ? (() => {
            const seen = new Set();
            const unique = [];

            selectedMessage.replies.forEach((reply) => {
                if (!seen.has(reply._id)) {
                    seen.add(reply._id);
                    unique.push(reply);
                }
            });

            return unique.sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
        })()
        : [];

    const latestReply = uniqueReplies.length > 0 ? uniqueReplies[0] : null;
    const olderReplies = uniqueReplies.slice(1);

    useEffect(() => {
        if (latestReply) {
            const isUnread = !latestReply.isRead && latestReply.sendedBy._id !== getCurrentUserId();

            if (isUnread) {
                const markAsRead = async () => {
                    await updateMessageReplyRead({ messageId: selectedMessage._id, replyId: latestReply._id });

                    const updatedReplies = selectedMessage.replies.map((r) =>
                        r._id === latestReply._id ? { ...r, isRead: true } : r
                    );
                    setSelectedMessage({ ...selectedMessage, replies: updatedReplies });

                    const listKey = "myInbox";
                    setMessages((prev) => ({
                        ...prev,
                        [listKey]: prev[listKey].map((msg) =>
                            msg._id === selectedMessage._id
                                ? {
                                    ...msg,
                                    unreadRepliesCount: Math.max(0, msg.unreadRepliesCount - 1),
                                    replies: msg.replies.map((r) => (r._id === latestReply._id ? { ...r, isRead: true } : r))
                                }
                                : msg
                        ),
                    }));
                };
                markAsRead();
            }
        }
    }, [latestReply?._id]);

    const renderMessageDetailsModal = () => {
        if (!selectedMessage) return null;
        const isInbox = activeTab === 1;
        const otherUser = isInbox
            ? selectedMessage?.sendedBy
            : selectedMessage?.sendTo;

        const handleExpandReply = async (replyId) => {
            if (!selectedMessage) return;

            const reply = selectedMessage.replies.find((r) => r._id === replyId);
            if (!reply) return;

            const expandedId = expandedReplyId === replyId ? null : replyId;
            setExpandedReplyId(expandedId);

            if (reply.isRead) return;

            await updateMessageReplyRead({ messageId: selectedMessage._id, replyId });

            const updatedReplies = selectedMessage.replies.map((r) =>
                r._id === replyId ? { ...r, isRead: true } : r
            );
            setSelectedMessage({ ...selectedMessage, replies: updatedReplies });

            const listKey = isInbox ? "myInbox" : "sendedMsg";

            setMessages((prev) => ({
                ...prev,
                [listKey]: prev[listKey].map((msg) =>
                    msg._id === selectedMessage._id
                        ? { ...msg, unreadRepliesCount: Math.max(0, msg.unreadRepliesCount - 1), replies: msg.replies.map((r) => (r._id === replyId ? { ...r, isRead: true } : r)) }
                        : msg
                ),
            }));
        };

        const handleExpandOriginal = () => {
            const expandedId = expandedReplyId === 'original' ? null : 'original';
            setExpandedReplyId(expandedId);
        };

        // Render original message (non-accordion)
        const renderOriginalMessage = () => {
            return (
                <section className="mb-2 w-full" data-reply-id="original">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-[11px] font-medium text-[#313C4C] uppercase tracking-wider">
                            Original Message
                        </h3>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-none p-2.5 space-y-1.5 shadow-xs w-full">
                        {/* Sender Info */}
                        <div className="flex items-center justify-between flex-wrap gap-2 pb-1.5 border-b border-slate-100 w-full">
                            <div className="flex items-center gap-2">
                                <Avatar className="!w-6 !h-6 !text-[10px] !bg-[#313C4C] !text-white !font-medium !rounded-none shadow-xs">
                                    {getInitials(otherUser.email)}
                                </Avatar>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-[11px] text-slate-900">
                                            {otherUser.firstName} {otherUser.lastName}
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-medium">{otherUser.email}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-50 px-2 py-0.5 rounded-none border border-slate-200/60">
                                <FaCalendarAlt className="w-3 h-3 text-slate-400" />
                                <span>{formatDate(selectedMessage?.createdAt)}</span>
                            </div>
                        </div>

                        {/* Title */}
                        <div className="w-full">
                            <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                Title
                            </div>
                            <div className="bg-[#f8fafc] border border-slate-200/90 rounded-none px-2 py-1 w-full shadow-2xs">
                                <p className="text-[11px] font-medium text-slate-900">
                                    {selectedMessage?.messageTitle || "(no subject)"}
                                </p>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="w-full">
                            <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                Subject / Content
                            </div>
                            <div className="bg-[#f8fafc] border border-slate-200/90 rounded-none p-2 max-h-[160px] overflow-y-auto scroll-thin w-full shadow-2xs">
                                <p className="text-[11px] text-slate-800 whitespace-pre-wrap break-words leading-relaxed font-medium">
                                    {selectedMessage?.messageBody || "(no subject)"}
                                </p>
                            </div>
                        </div>

                        {/* Attachment */}
                        {selectedMessage?.file && (
                            <div className="w-full">
                                <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                    Attachment
                                </div>
                                <div className="w-full border border-slate-200 rounded-none flex items-center gap-2 p-1.5 pr-3 relative bg-[#f8fafc] shadow-2xs">
                                    <div
                                        className={`w-6 h-6 ${getFileTypeClass(
                                            selectedMessage?.fileType
                                        )} rounded-none flex items-center justify-center flex-shrink-0 shadow-xs`}
                                    >
                                        <FaFileAlt className="w-3 h-3 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[11px] font-medium text-slate-800 truncate">
                                            {selectedMessage?.docType?.toUpperCase()}-
                                            {selectedMessage?.file.split("/").pop()}
                                        </div>
                                        <div className="text-[10px] text-slate-500 font-medium">
                                            {selectedMessage?.fileType?.toUpperCase()} Document
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            handleDownload(
                                                selectedMessage?.file,
                                                `${selectedMessage?.docType}-${selectedMessage?.file
                                                    .split("/")
                                                    .pop()
                                                    .split(".")[0] || "document"
                                                }`
                                            )
                                        }
                                        className="flex items-center justify-center w-6 h-6 rounded-none bg-white border border-slate-200 hover:bg-slate-100 transition shadow-2xs"
                                    >
                                        <FaDownload className="w-2.5 h-2.5 text-slate-700" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Reply Button */}
                        <div className="flex gap-2 pt-0.5 w-full">
                            <button
                                onClick={() =>
                                    handleOpenReply({
                                        ...selectedMessage,
                                        replyMessageId: selectedMessage._id,
                                    })
                                }
                                className="h-7 px-3 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e293b] rounded-none uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm"
                            >
                                <IoRepeat className="w-3.5 h-3.5" />
                                <span>REPLY</span>
                            </button>
                        </div>
                    </div>
                </section>
            );
        };

        // Render latest reply (non-accordion)
        const renderLatestReply = () => {
            if (!latestReply) return null;
            const isUnread = !latestReply.isRead && latestReply.sendedBy._id !== getCurrentUserId();

            return (
                <section className="mb-2 w-full">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-[11px] font-medium text-[#313C4C] uppercase tracking-wider">
                            Latest Reply
                        </h3>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-none p-2.5 space-y-1.5 shadow-xs w-full">
                        {/* Sender Info */}
                        <div className="flex items-center justify-between flex-wrap gap-2 pb-1.5 border-b border-slate-100 w-full">
                            <div className="flex items-center gap-2">
                                <Avatar className="!w-6 !h-6 !text-[10px] !bg-[#313C4C] !text-white !font-medium !rounded-none shadow-xs">
                                    {getInitials(latestReply.sendedBy.email)}
                                </Avatar>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-[11px] text-slate-900">
                                            {latestReply.sendedBy.firstName}{" "}
                                            {latestReply.sendedBy.lastName}
                                        </span>
                                        {isUnread && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-none bg-rose-600 text-white font-medium animate-pulse">
                                                UNREAD
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-medium">{latestReply.sendedBy.email}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-50 px-2 py-0.5 rounded-none border border-slate-200/60">
                                <FaCalendarAlt className="w-3 h-3 text-slate-400" />
                                <span>{formatDate(latestReply.createdAt)}</span>
                            </div>
                        </div>

                        {/* Title */}
                        <div className="w-full">
                            <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                Title
                            </div>
                            <div className="bg-[#f8fafc] border border-slate-200/90 rounded-none px-2 py-1 w-full shadow-2xs">
                                <p className="text-[11px] font-medium text-slate-900">
                                    {latestReply.messageTitle || "(no subject)"}
                                </p>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="w-full">
                            <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                Subject / Content
                            </div>
                            <div className="bg-[#f8fafc] border border-slate-200/90 rounded-none p-2 max-h-[160px] overflow-y-auto scroll-thin w-full shadow-2xs">
                                <p className="text-[11px] text-slate-800 whitespace-pre-wrap break-words leading-relaxed font-medium">
                                    {latestReply.messageBody || "(no subject)"}
                                </p>
                            </div>
                        </div>

                        {/* Attachment */}
                        {latestReply.file && (
                            <div className="w-full">
                                <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                    Attachment
                                </div>
                                <div className="w-full border border-slate-200 rounded-none flex items-center gap-2 p-1.5 pr-3 relative bg-[#f8fafc] shadow-2xs">
                                    <div
                                        className={`w-6 h-6 ${getFileTypeClass(
                                            latestReply.fileType
                                        )} rounded-none flex items-center justify-center flex-shrink-0 shadow-xs`}
                                    >
                                        <FaFileAlt className="w-3 h-3 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[11px] font-medium text-slate-800 truncate">
                                            {latestReply.docType?.toUpperCase()}-
                                            {latestReply.file.split("/").pop()}
                                        </div>
                                        <div className="text-[10px] text-slate-500 font-medium">
                                            {latestReply.fileType?.toUpperCase()} Document
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            handleDownload(
                                                latestReply.file,
                                                `${latestReply.docType}-${latestReply.file
                                                    .split("/")
                                                    .pop()
                                                    .split(".")[0] || "document"
                                                }`
                                            )
                                        }
                                        className="flex items-center justify-center w-6 h-6 rounded-none bg-white border border-slate-200 hover:bg-slate-100 transition shadow-2xs"
                                    >
                                        <FaDownload className="w-2.5 h-2.5 text-slate-700" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Reply Button */}
                        <div className="flex gap-2 pt-0.5 w-full">
                            <button
                                onClick={() =>
                                    handleOpenReply({
                                        ...latestReply,
                                        _id: selectedMessage._id,
                                        replyMessageId: latestReply._id,
                                    })
                                }
                                className="h-7 px-3 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e293b] rounded-none uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm"
                            >
                                <IoRepeat className="w-3.5 h-3.5" />
                                <span>REPLY</span>
                            </button>
                        </div>

                        {/* Reply Reference */}
                        {latestReply.replyOf && (
                            <>
                                <Divider className="!my-1.5" />
                                <div className="bg-[#f1f5f9]/80 border border-slate-200 rounded-none p-2 w-full shadow-2xs">
                                    <div className="flex items-start justify-between gap-2 w-full">
                                        <div className="flex-1">
                                            <div className="text-[10px] font-medium text-[#313C4C] uppercase mb-0.5 tracking-wider">
                                                In Reply To:
                                            </div>
                                            <div className="space-y-0.5">
                                                <div className="flex items-center gap-1.5">
                                                    <Avatar className="!w-4.5 !h-4.5 !text-[9px] !bg-[#313C4C] !text-white !font-medium !rounded-none">
                                                        {getInitials(
                                                            latestReply.replyOf.sendedBy.email
                                                        )}
                                                    </Avatar>
                                                    <span className="text-[11px] font-medium text-slate-900">
                                                        {latestReply.replyOf.sendedBy.firstName}{" "}
                                                        {latestReply.replyOf.sendedBy.lastName}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-slate-800 font-medium line-clamp-2">
                                                    {latestReply.replyOf.messageTitle ||
                                                        "(no subject)"}
                                                </p>
                                                <p className="text-[10px] text-slate-500 font-medium">
                                                    {formatDate(latestReply.replyOf.createdAt)}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (latestReply.replyOf._id === selectedMessage._id) {
                                                    setExpandedReplyId('original');
                                                    setTimeout(() => {
                                                        document.querySelector('[data-reply-id="original"]')?.scrollIntoView({
                                                            behavior: "smooth",
                                                            block: "center",
                                                        });
                                                    }, 100);
                                                } else {
                                                    const replyElement = document.querySelector(
                                                        `[data-reply-id="${latestReply.replyOf._id}"]`
                                                    );
                                                    if (replyElement) {
                                                        const accordionButton = replyElement.querySelector(
                                                            '[aria-expanded="false"]'
                                                        );
                                                        if (accordionButton) accordionButton.click();

                                                        requestAnimationFrame(() => {
                                                            replyElement.scrollIntoView({
                                                                behavior: "smooth",
                                                                block: "center",
                                                            });
                                                        });
                                                    }
                                                }
                                            }}
                                            className="h-7 px-3 text-[11px] font-medium text-[#313C4C] hover:bg-[#313C4C] hover:text-white bg-white rounded-none uppercase whitespace-nowrap transition-all border border-[#313C4C] shadow-sm flex items-center justify-center"
                                        >
                                            VIEW MESSAGE
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>
            );
        };

        const totalHistoryMessages = latestReply ? olderReplies.length + 1 : 1;

        return (
            <div className="p-1 sm:p-1.5 flex-1 overflow-y-auto space-y-2 scroll-thin w-full bg-slate-50/40">
                {/* Show Original Message if no replies, otherwise show Latest Reply */}
                {latestReply ? renderLatestReply() : renderOriginalMessage()}

                {/* Conversation History Header */}
                {(latestReply || uniqueReplies.length > 0) && (
                    <>
                        <div className="flex items-center gap-2 mb-2 w-full">
                            <h3 className="text-[11px] font-medium text-[#313C4C] uppercase tracking-wider">
                                Conversation History
                            </h3>
                            <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700 text-[10px] font-medium rounded-none">
                                {totalHistoryMessages} {totalHistoryMessages === 1 ? 'message' : 'messages'}
                            </span>
                        </div>

                        {/* Older Replies as Accordions */}
                        {olderReplies.map((reply) => {
                            const isUnread = !reply.isRead && reply.sendedBy._id !== getCurrentUserId();

                            return (
                                <div key={reply._id} data-reply-id={reply._id} className="w-full">
                                    <Accordion
                                        elevation={0}
                                        expanded={expandedReplyId === reply._id}
                                        onChange={() => handleExpandReply(reply._id)}
                                        className="!border !border-slate-200 !rounded-none !mb-2 !transition-all hover:!border-slate-300 before:!hidden !w-full overflow-hidden shadow-2xs"
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore fontSize="small" />}
                                            className="!min-h-[36px] hover:!bg-slate-50 !py-1 !px-2.5 !w-full"
                                        >
                                            <div className="flex items-center gap-2.5 w-full pr-2">
                                                <Avatar className="!w-6 !h-6 !bg-[#313C4C] !text-white !text-[10px] !font-medium !rounded-none">
                                                    {getInitials(reply.sendedBy.email)}
                                                </Avatar>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-[11px] text-slate-900">
                                                            {reply.sendedBy.firstName}{" "}
                                                            {reply.sendedBy.lastName}
                                                        </span>
                                                        {isUnread && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded-none bg-rose-600 text-white font-medium animate-pulse">
                                                                NEW
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-[11px] text-slate-500 font-medium truncate max-w-[320px]">
                                                        {reply.messageTitle || "(no subject)"}
                                                    </div>
                                                </div>

                                                <div className="flex flex-col items-end">
                                                    <span className="text-[10px] font-medium text-slate-500">
                                                        {formatDate(reply.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        </AccordionSummary>

                                        <AccordionDetails className="!pt-1 !px-2.5 !pb-2.5 !bg-slate-50/50 !w-full !block">
                                            <Divider className="!mb-2" />

                                            <div className="space-y-2 w-full">
                                                {/* Message Title */}
                                                <div className="bg-white rounded-none p-2 border border-slate-200 w-full shadow-2xs">
                                                    <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                                        Title
                                                    </div>
                                                    <p className="text-[11px] font-medium text-slate-900">
                                                        {reply.messageTitle || "(no subject)"}
                                                    </p>
                                                </div>

                                                {/* Message Body */}
                                                <div className="bg-white rounded-none p-2.5 border border-slate-200 w-full shadow-2xs">
                                                    <div className="text-[10px] font-medium text-slate-500 uppercase mb-1 tracking-widest">
                                                        Subject / Content
                                                    </div>
                                                    <p className="text-[11px] text-slate-800 whitespace-pre-wrap break-words leading-relaxed max-h-[160px] overflow-y-auto scroll-thin font-medium">
                                                        {reply.messageBody || "(no subject)"}
                                                    </p>
                                                </div>

                                                {/* Reply Attachment */}
                                                {reply.file && (
                                                    <Paper elevation={0} className="inline-block w-full rounded-none overflow-hidden">
                                                        <div className="w-full border border-slate-200 rounded-none flex items-center gap-2.5 p-2 bg-white shadow-2xs">
                                                            <div
                                                                className={`w-7 h-7 ${getFileTypeClass(
                                                                    reply.fileType
                                                                )} rounded-none flex items-center justify-center flex-shrink-0 shadow-xs`}
                                                            >
                                                                <FaFileAlt className="w-3.5 h-3.5 text-white" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-[11px] font-medium text-slate-800 truncate">
                                                                    {reply.docType?.toUpperCase()}-
                                                                    {reply.file.split("/").pop()}
                                                                </div>
                                                                <div className="text-[10px] text-slate-500 font-medium">
                                                                    {reply.fileType?.toUpperCase()} Document
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() =>
                                                                    handleDownload(
                                                                        reply.file,
                                                                        `${reply.docType}-${reply.file
                                                                            .split("/")
                                                                            .pop()
                                                                            .split(".")[0] || "document"
                                                                        }`
                                                                    )
                                                                }
                                                                className="flex items-center justify-center w-7 h-7 rounded-none bg-slate-50 border border-slate-200 hover:bg-slate-100 transition shadow-2xs"
                                                            >
                                                                <FaDownload className="w-3 h-3 text-slate-700" />
                                                            </button>
                                                        </div>
                                                    </Paper>
                                                )}

                                                {/* Recipient info */}
                                                <div className="flex items-center gap-2 text-[11px] text-slate-500 px-1 w-full font-medium">
                                                    <span className="font-medium uppercase text-[10px] tracking-wider">To:</span>
                                                    <Avatar className="!w-5 !h-5 !text-[10px] !bg-slate-500 !rounded-none">
                                                        {getInitials(reply.sendTo.email)}
                                                    </Avatar>
                                                    <span className="text-slate-800 font-medium text-[11px]">
                                                        {reply.sendTo.firstName} {reply.sendTo.lastName}
                                                    </span>
                                                </div>

                                                {/* Reply to Reply Button */}
                                                <div className="flex gap-2 pt-1 w-full">
                                                    <button
                                                        onClick={() =>
                                                            handleOpenReply({
                                                                ...reply,
                                                                _id: selectedMessage._id,
                                                                replyMessageId: reply._id,
                                                            })
                                                        }
                                                        className="h-7 px-3 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e293b] rounded-none uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm"
                                                    >
                                                        <IoRepeat className="w-3.5 h-3.5" />
                                                        <span>REPLY</span>
                                                    </button>
                                                </div>

                                                {/* Reply Reference */}
                                                {reply.replyOf && (
                                                    <>
                                                        <Divider className="!my-2" />
                                                        <div className="bg-slate-100/80 border border-slate-200 rounded-none p-2.5 w-full shadow-2xs">
                                                            <div className="flex items-start justify-between gap-2.5 w-full">
                                                                <div className="flex-1">
                                                                    <div className="text-[11px] font-medium text-[#313C4C] uppercase mb-1 tracking-wider">
                                                                        In Reply To:
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <Avatar className="!w-5 !h-5 !text-[10px] !bg-[#313C4C] !text-white !font-medium !rounded-none">
                                                                                {getInitials(
                                                                                    reply.replyOf.sendedBy.email
                                                                                )}
                                                                            </Avatar>
                                                                            <span className="text-[11px] font-medium text-slate-900">
                                                                                {reply.replyOf.sendedBy.firstName}{" "}
                                                                                {reply.replyOf.sendedBy.lastName}
                                                                            </span>
                                                                        </div>
                                                                        <p className="text-[11px] text-slate-800 font-medium line-clamp-2">
                                                                            {reply.replyOf.messageTitle ||
                                                                                "(no subject)"}
                                                                        </p>
                                                                        <p className="text-[10px] text-slate-500 font-medium">
                                                                            {formatDate(reply.replyOf.createdAt)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => {
                                                                        if (reply.replyOf._id === selectedMessage._id) {
                                                                            setExpandedReplyId('original');
                                                                            setTimeout(() => {
                                                                                document.querySelector('[data-reply-id="original"]')?.scrollIntoView({
                                                                                    behavior: "smooth",
                                                                                    block: "center",
                                                                                });
                                                                            }, 100);
                                                                        } else {
                                                                            const replyElement = document.querySelector(
                                                                                `[data-reply-id="${reply.replyOf._id}"]`
                                                                            );

                                                                            if (replyElement) {
                                                                                const accordionButton = replyElement.querySelector(
                                                                                    '[aria-expanded="false"]'
                                                                                );
                                                                                if (accordionButton) {
                                                                                    accordionButton.click();
                                                                                }

                                                                                requestAnimationFrame(() => {
                                                                                    replyElement.scrollIntoView({
                                                                                        behavior: "smooth",
                                                                                        block: "center",
                                                                                    });
                                                                                });
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="h-7 px-3 text-[11px] font-medium text-[#313C4C] hover:bg-[#313C4C] hover:text-white bg-white rounded-none uppercase whitespace-nowrap transition-all border border-[#313C4C] shadow-sm flex items-center justify-center"
                                                                >
                                                                    VIEW MESSAGE
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                </div>
                            );
                        })}

                        {/* Original Message as Accordion - Only show if there are replies */}
                        {latestReply && (
                            <div data-reply-id="original" className="w-full">
                                <Accordion
                                    elevation={0}
                                    expanded={expandedReplyId === 'original'}
                                    onChange={handleExpandOriginal}
                                    className="!border !border-slate-200 !rounded-none !mb-2 !transition-all hover:!border-slate-300 before:!hidden !w-full overflow-hidden shadow-2xs"
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore fontSize="small" />}
                                        className="!min-h-[36px] hover:!bg-slate-50 !py-1 !px-2.5 !w-full"
                                    >
                                        <div className="flex items-center gap-2.5 w-full pr-2">
                                            <Avatar className="!w-6 !h-6 !bg-slate-600 !text-white !text-[10px] !font-medium !rounded-none">
                                                {getInitials(otherUser.email)}
                                            </Avatar>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-[11px] text-slate-900">
                                                        {otherUser.firstName} {otherUser.lastName}
                                                    </span>
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-none bg-slate-200 text-slate-700 font-medium">
                                                        ORIGINAL
                                                    </span>
                                                </div>
                                                <div className="text-[11px] text-slate-500 font-medium truncate max-w-[320px]">
                                                    {selectedMessage?.messageTitle || "(no subject)"}
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-end">
                                                <span className="text-[10px] font-medium text-slate-500">
                                                    {formatDate(selectedMessage?.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    </AccordionSummary>

                                    <AccordionDetails className="!pt-1 !px-2.5 !pb-2.5 !bg-slate-50/50 !w-full !block">
                                        <Divider className="!mb-2" />

                                        <div className="space-y-2 w-full">
                                            {/* Title */}
                                            <div className="bg-white rounded-none p-2 border border-slate-200 w-full shadow-2xs">
                                                <div className="text-[10px] font-medium text-slate-500 uppercase mb-0.5 tracking-widest">
                                                    Title
                                                </div>
                                                <p className="text-[11px] font-medium text-slate-900">
                                                    {selectedMessage?.messageTitle || "(no subject)"}
                                                </p>
                                            </div>

                                            {/* Body */}
                                            <div className="bg-white rounded-none p-2.5 border border-slate-200 w-full shadow-2xs">
                                                <div className="text-[10px] font-medium text-slate-500 uppercase mb-1 tracking-widest">
                                                    Subject / Content
                                                </div>
                                                <p className="text-[11px] text-slate-800 whitespace-pre-wrap break-words leading-relaxed max-h-[160px] overflow-y-auto scroll-thin font-medium">
                                                    {selectedMessage?.messageBody || "(no subject)"}
                                                </p>
                                            </div>

                                            {/* Attachment */}
                                            {selectedMessage?.file && (
                                                <div className="bg-white rounded-none p-2 border border-slate-200 w-full shadow-2xs">
                                                    <div className="text-[10px] font-medium text-slate-500 uppercase mb-1 tracking-widest">
                                                        Attachment
                                                    </div>
                                                    <div className="w-full border border-slate-200 rounded-none flex items-center gap-2.5 p-2 bg-slate-50 shadow-2xs">
                                                        <div
                                                            className={`w-7 h-7 ${getFileTypeClass(
                                                                selectedMessage?.fileType
                                                            )} rounded-none flex items-center justify-center flex-shrink-0 shadow-xs`}
                                                        >
                                                            <FaFileAlt className="w-3.5 h-3.5 text-white" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-[11px] font-medium text-slate-800 truncate">
                                                                {selectedMessage?.docType?.toUpperCase()}-
                                                                {selectedMessage?.file.split("/").pop()}
                                                            </div>
                                                            <div className="text-[10px] text-slate-500 font-medium">
                                                                {selectedMessage?.fileType?.toUpperCase()} Document
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() =>
                                                                handleDownload(
                                                                    selectedMessage?.file,
                                                                    `${selectedMessage?.docType}-${selectedMessage?.file.split("/").pop().split(".")[0] ||
                                                                    "document"
                                                                    }`
                                                                )
                                                            }
                                                            className="flex items-center justify-center w-7 h-7 rounded-none bg-white border border-slate-200 hover:bg-slate-100 transition shadow-2xs"
                                                        >
                                                            <FaDownload className="w-3 h-3 text-slate-700" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Reply Button */}
                                            <div className="flex gap-2 pt-1 w-full">
                                                <button
                                                    onClick={() =>
                                                        handleOpenReply({
                                                            ...selectedMessage,
                                                            replyMessageId: selectedMessage._id,
                                                        })
                                                    }
                                                    className="h-7 px-3 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e293b] rounded-none uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm"
                                                >
                                                    <IoRepeat className="w-3.5 h-3.5" />
                                                    <span>REPLY</span>
                                                </button>
                                            </div>
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };
    const isDisable = mode === "send"
        ? !docType || !selectedUser._id || !file || !messageTitle
        : !messageBody || !messageTitle || !docType
    return (
        <div className="min-h-[calc(100vh-110px)] text-slate-800 p-1 sm:p-2 bg-slate-50/50">
            <div className="max-w-[1350px] mx-auto">
                {/* Header Card */}
                <div className="bg-white rounded-none border border-slate-200 shadow-sm overflow-hidden mb-2">
                    <div className="flex flex-wrap gap-2 justify-between items-center border-b border-slate-200 p-1.5 sm:p-2 px-2.5 bg-slate-50">
                        <CustomTabs
                            tabs={tabs}
                            active={activeTab}
                            setActive={setActiveTab}
                            unreadCounts={unreadCounts}
                        />
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="h-7 px-3 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e293b] rounded-none uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm"
                        >
                            <FaRegPaperPlane className="w-3 h-3" />
                            <span>SEND MESSAGE</span>
                        </button>
                    </div>

                    <div className="p-1 sm:p-1.5">
                        {activeTab === 1
                            ? renderMessageList(messages?.myInbox, true)
                            : renderMessageList(messages?.sendedMsg, false)}
                    </div>
                </div>
            </div>

            {/* Send Message Modal */}
            <Dialog
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setMode("send");
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    className: "!rounded-none shadow-xl border border-slate-200 overflow-hidden",
                }}
            >
                {/* Header */}
                <div className="bg-[#f8fafc] text-slate-900 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
                    <div className="flex items-center gap-2">
                        <FaRegPaperPlane className="w-3 h-3 text-[#313C4C]" />
                        <h2 className="text-[11px] font-medium text-slate-900 uppercase tracking-wider">
                            {mode === "reply" ? "Reply to Message" : "Send New Message"}
                        </h2>
                        {mode === "reply" && selectedMessage?.sendedBy?.email && (
                            <span className="text-[11px] text-slate-600 font-medium ml-2">
                                ({selectedMessage?.sendedBy?.email})
                            </span>
                        )}
                    </div>
                    <IconButton
                        onClick={() => {
                            setIsModalOpen(false);
                            resetForm();
                            setMode("send");
                        }}
                        size="small"
                        className="!text-slate-500 hover:!text-slate-800"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </div>

                <DialogContent className="!p-3.5 sm:!p-4 !bg-slate-50/40">
                    <div className="space-y-2.5">
                        {/* Document Type */}
                        <div>
                            <label className="block text-[11px] font-medium text-slate-600 uppercase tracking-widest mb-1">
                                Document Type <span className="text-rose-500">*</span>
                            </label>
                            <TextField
                                select
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={docType}
                                onChange={(e) => setDocType(e.target.value)}
                                SelectProps={{
                                    className: "!text-[11px] !py-0.5 !font-medium",
                                }}
                                InputProps={{
                                    className: "!text-[11px] !bg-white !rounded-none shadow-2xs !font-medium",
                                }}
                            >
                                <MenuItem value="" disabled className="!text-[11px] !font-medium">Select Document Type</MenuItem>
                                <MenuItem value="sof" className="!text-[11px] !font-medium">SOF</MenuItem>
                                <MenuItem value="laytime" className="!text-[11px] !font-medium">Laytime</MenuItem>
                                <MenuItem value="cargo" className="!text-[11px] !font-medium">Cargo</MenuItem>
                                <MenuItem value="vessel" className="!text-[11px] !font-medium">Vessel</MenuItem>
                                <MenuItem value="invoice" className="!text-[11px] !font-medium">Invoice</MenuItem>
                                <MenuItem value="contracts" className="!text-[11px] !font-medium">Contracts</MenuItem>
                                <MenuItem value="address" className="!text-[11px] !font-medium">Address Book</MenuItem>
                                <MenuItem value="matrix" className="!text-[11px] !font-medium">Matrix</MenuItem>
                            </TextField>
                        </div>

                        {/* Send To Section */}
                        {mode === "send" && (
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-[11px] font-medium text-slate-600 uppercase tracking-widest">
                                        Send To <span className="text-rose-500">*</span>
                                    </label>
                                    <RadioGroup
                                        row
                                        value={sendTo}
                                        onChange={(e) => setSendTo(e.target.value)}
                                        className="!my-0"
                                    >
                                        <FormControlLabel
                                            value="anyone"
                                            control={<Radio size="small" className="!p-1" />}
                                            label={<span className="text-[11px] font-medium text-slate-700">Any User</span>}
                                        />
                                        <FormControlLabel
                                            value="org"
                                            control={<Radio size="small" className="!p-1" />}
                                            label={<span className="text-[11px] font-medium text-slate-700">Org User</span>}
                                        />
                                    </RadioGroup>
                                </div>

                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder={`Type email to search ${sendTo === "org" ? "organization user" : "user"}...`}
                                    value={searchTerm || selectedUser.email}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setSelectedUser({ email: "", _id: "" });
                                    }}
                                    InputProps={{
                                        className: "!text-[11px] !bg-white !rounded-none shadow-2xs !font-medium",
                                    }}
                                />

                                {searchTerm.length > 0 && (
                                    <div className="border border-slate-200 rounded-none overflow-hidden mt-1 shadow-md max-h-36 overflow-y-auto scroll-thin bg-white z-10 relative">
                                        {filteredUsers.length > 0 ? (
                                            filteredUsers.map((user, index) => (
                                                <div key={user._id}>
                                                    <div
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setSearchTerm("");
                                                        }}
                                                        className="px-2.5 py-1.5 cursor-pointer hover:bg-slate-100 transition-colors flex items-center gap-2"
                                                    >
                                                        <Avatar className="!w-5 !h-5 !text-[10px] !bg-[#313C4C] !text-white !font-medium !rounded-none">
                                                            {user.email[0].toUpperCase()}
                                                        </Avatar>
                                                        <div>
                                                            <p className="text-[11px] font-medium text-slate-800">
                                                                {user.name}
                                                            </p>
                                                            <p className="text-[11px] font-medium text-slate-500">
                                                                {user.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {index < filteredUsers.length - 1 && <Divider />}
                                                </div>
                                            ))
                                        ) : hasFetched ? (
                                            <div className="px-3 py-2 text-center">
                                                <p className="text-[11px] font-medium text-slate-500">No users found</p>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Message Title */}
                        <div>
                            <label className="block text-[11px] font-medium text-slate-600 uppercase tracking-widest mb-1">
                                Message Title <span className="text-rose-500">*</span>
                            </label>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Enter message title"
                                value={
                                    mode === "reply" && selectedMessage
                                        ? `${messageTitle ?? selectedMessage?.messageTitle}`
                                        : messageTitle
                                }
                                onChange={(e) => setMessageTitle(e.target.value)}
                                InputProps={{
                                    className: "!text-[11px] !bg-white !rounded-none shadow-2xs !font-medium",
                                }}
                            />
                        </div>

                        {/* Message Body */}
                        <div>
                            <label className="block text-[11px] font-medium text-slate-600 uppercase tracking-widest mb-1">
                                {mode === "reply" ? "Reply Message" : "Message Body"} <span className="text-rose-500">*</span>
                            </label>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                placeholder={
                                    mode === "reply"
                                        ? "Type your reply message..."
                                        : "Enter your message..."
                                }
                                value={messageBody}
                                onChange={(e) => setMessageBody(e.target.value)}
                                InputProps={{
                                    className: "!text-[11px] !bg-white !rounded-none shadow-2xs !font-medium",
                                }}
                            />
                        </div>

                        {/* File Upload */}
                        <div>
                            <label className="block text-[11px] font-medium text-slate-600 uppercase tracking-widest mb-1">
                                Attachment {mode === "send" && <span className="text-rose-500">*</span>}
                            </label>
                            <FileUploader
                                handleChange={handleChange}
                                name="file"
                                types={["PDF", "DOCX", "XLSX", "TXT", "CSV"]}
                                maxSize={10}
                                classes="custom-dropzone"
                            >
                                {file ? (
                                    <div className="border border-slate-200 bg-white rounded-none p-2 cursor-pointer transition-all shadow-2xs">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Avatar className="!w-6 !h-6 !bg-[#313C4C] !rounded-none shadow-xs">
                                                    <AttachFile className="text-white" style={{ fontSize: 13 }} />
                                                </Avatar>
                                                <div>
                                                    <p className="text-[11px] font-medium text-slate-800">
                                                        {file.name}
                                                    </p>
                                                    <p className="text-[10px] font-medium text-slate-500">
                                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                                    </p>
                                                </div>
                                            </div>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setFile(null);
                                                }}
                                                className="text-slate-500 hover:text-slate-800"
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="border border-dashed border-slate-300 rounded-none p-3 flex gap-2.5 justify-center items-center text-center cursor-pointer transition-all hover:border-[#313C4C] hover:bg-slate-100/70 group bg-white shadow-2xs">
                                        <CloudUpload className="w-4 h-4 text-slate-400 group-hover:text-[#313C4C] transition-colors" />
                                        <div>
                                            <p className="text-[11px] font-medium text-slate-700 group-hover:text-[#313C4C]">
                                                Click to upload or drag & drop file
                                            </p>
                                            <p className="text-[10px] font-medium text-slate-400">
                                                PDF, DOCX, XLSX, TXT, CSV (max 10MB)
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </FileUploader>
                        </div>
                    </div>
                </DialogContent>

                <Divider />

                <DialogActions className="px-4 py-2 bg-slate-50 gap-2">
                    <button
                        onClick={() => {
                            setIsModalOpen(false);
                            resetForm();
                            setMode("send");
                        }}
                        className="h-7 px-3.5 text-[11px] font-medium text-slate-700 bg-slate-200 hover:bg-slate-300 rounded-none uppercase tracking-wider transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            const isValid =
                                mode === "send"
                                    ? docType && selectedUser._id && file && messageTitle
                                    : messageBody;

                            if (isValid) {
                                mode === "reply" ? handleSendReply(file) : handleSend();
                            }
                        }}
                        disabled={isDisable}
                        className={`h-7 px-4 text-[11px] font-medium text-white rounded-none uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm ${isDisable
                                ? 'bg-slate-400 cursor-not-allowed opacity-60'
                                : 'bg-[#313C4C] hover:bg-[#1e293b]'
                            }`}
                    >
                        {mode === "reply" ? (
                            <>
                                <Reply style={{ fontSize: 14 }} />
                                <span>SEND REPLY</span>
                            </>
                        ) : (
                            <>
                                <Send style={{ fontSize: 14 }} />
                                <span>SEND MESSAGE</span>
                            </>
                        )}
                    </button>
                </DialogActions>
            </Dialog>

            {/* Message Details Modal */}
            <CustomShowModal
                open={selectedMessage}
                handleClose={handleCloseDetailModal}
                title={`Message Details (${selectedMessage?.docType})`}
                maxWidth="lg"
                contentClassName="!p-1.5 sm:!p-2 bg-white"
            >
                {renderMessageDetailsModal()}
            </CustomShowModal>
        </div>
    );
};

export default Messagebox;